"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";

import {
  CreateNotificationResponse,
  ListNotificationsQuery,
  ListNotificationsResponse,
  CreateNotificationBody,
  EditNotificationBody,
  GetNotificationResponse,
} from "@/libs/notifications/redux/notifications.types";
import { defined } from "@/libs/shared/utils/params";

const tagTypes = ["Notification" as const, "Notifications" as const];

export const invalidateNotifications = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) =>
        api.dispatch(notificationsApi.util.invalidateTags([tag])),
      );
    });
  };
};

export const notificationsApi = createApi({
  reducerPath: "notificationsApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getNotifications: builder.query<
      ListNotificationsResponse,
      ListNotificationsQuery
    >({
      query: (query) => {
        const searchParams = new URLSearchParams(defined(query));
        return { url: `pt/custom-notifications?${searchParams?.toString()}` };
      },
      providesTags: ["Notifications"],
    }),
    getNotification: builder.query<GetNotificationResponse, { id: string }>({
      query: ({ id }) => ({
        url: `pt/custom-notifications/${id}`,
      }),
      providesTags: ["Notification"],
    }),
    createNotification: builder.mutation<
      CreateNotificationResponse,
      CreateNotificationBody
    >({
      query: (notification) => {
        return {
          url: "pt/custom-notifications",
          method: "POST",
          body: notification,
        };
      },
      onQueryStarted: invalidateNotifications([
        "Notification",
        "Notifications",
      ]),
    }),
    editNotification: builder.mutation<{ id: number }, EditNotificationBody>({
      query: (notification) => {
        return {
          url: `pt/custom-notifications/${notification?.id}`,
          method: "POST",
          body: notification,
        };
      },
      onQueryStarted: invalidateNotifications([
        "Notification",
        "Notifications",
      ]),
    }),
    deleteNotification: builder.mutation<void, string>({
      query: (id) => {
        return {
          url: `pt/custom-notifications/${id}`,
          method: "DELETE",
        };
      },
      onQueryStarted: invalidateNotifications(["Notifications"]),
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useCreateNotificationMutation,
  useGetNotificationQuery,
  useEditNotificationMutation,
  useDeleteNotificationMutation,
} = notificationsApi;
